import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Package, Tag, Image, ChevronRight, Eye, EyeOff, ShoppingBag } from 'lucide-react';
import { useAdminAuth } from '../AuthContext';
import { adminGetDashboard, adminGetOrderPipeline } from '../../services/api';

const CATEGORY_COLORS = {
  suppressors: 'text-orange-400 bg-orange-500/10',
  sme: 'text-blue-400 bg-blue-500/10',
  'clamp-ons': 'text-green-400 bg-green-500/10',
  'muzzle-devices': 'text-purple-400 bg-purple-500/10',
};

export default function AdminDashboard() {
  const { token } = useAdminAuth();
  const [stats, setStats] = useState(null);
  const [pipeline, setPipeline] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([adminGetDashboard(token), adminGetOrderPipeline(token)])
      .then(([s, p]) => { setStats(s); setPipeline(p); })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [token]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-orange-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const p = stats?.products || {};
  const c = stats?.campaigns || {};
  const m = stats?.media || {};
  const o = stats?.orders || {};
  const pipelineNeedsAttn = (pipeline?.needs_attention) || 0;

  return (
    <div data-testid="admin-dashboard">
      {/* Page title */}
      <div className="mb-8">
        <h1 className="font-bebas text-4xl text-white tracking-wider">DASHBOARD</h1>
        <p className="font-inter text-sm text-zinc-500 mt-1">Welcome back. Here's your content overview.</p>
      </div>

      {/* Stats grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {[
          { label: 'Total Products', value: p.total || 0, sub: `${p.active || 0} active · ${p.hidden || 0} hidden`, icon: Package, color: 'text-orange-500', href: '/admin/products' },
          { label: 'Featured', value: p.featured || 0, sub: 'Shown on homepage', icon: Package, color: 'text-yellow-500', href: '/admin/products' },
          { label: 'Active Campaigns', value: c.active || 0, sub: `${c.total || 0} total campaigns`, icon: Tag, color: 'text-green-500', href: '/admin/campaigns' },
          { label: 'Media Files', value: m.total || 0, sub: 'Uploaded assets', icon: Image, color: 'text-blue-500', href: '/admin/media' },
          { label: 'Total Orders', value: o.total || 0, sub: `${o.new || 0} new · ${o.ready || 0} ready`, icon: ShoppingBag, color: 'text-teal-500', href: '/admin/orders' },
          { label: 'Needs Attention', value: pipelineNeedsAttn, sub: 'Hold + validation issues', icon: ShoppingBag, color: pipelineNeedsAttn > 0 ? 'text-red-500' : 'text-zinc-500', href: '/admin/orders' },
        ].map(s => {
          const Icon = s.icon;
          return (
            <Link
              key={s.label}
              to={s.href}
              data-testid={`stat-card-${s.label.toLowerCase().replace(/\s/g, '-')}`}
              className="bg-[#1a1a1a] border border-white/10 p-5 hover:border-orange-500/30 transition-colors group"
            >
              <div className="flex items-start justify-between mb-3">
                <Icon size={18} className={`${s.color} group-hover:scale-110 transition-transform`} />
                <ChevronRight size={14} className="text-zinc-700 group-hover:text-zinc-400 transition-colors" />
              </div>
              <div className={`font-bebas text-3xl tracking-wider ${s.color}`}>{s.value}</div>
              <div className="font-inter text-xs text-white mt-0.5">{s.label}</div>
              <div className="font-inter text-[11px] text-zinc-600 mt-0.5">{s.sub}</div>
            </Link>
          );
        })}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent products */}
        <div className="bg-[#1a1a1a] border border-white/10">
          <div className="flex items-center justify-between px-5 py-4 border-b border-white/10">
            <h2 className="font-bebas text-lg text-white tracking-wider">RECENT PRODUCTS</h2>
            <Link to="/admin/products" className="font-inter text-xs text-orange-500 hover:text-orange-400">View all →</Link>
          </div>
          <div className="divide-y divide-white/5">
            {(stats?.recent_products || []).map((p, i) => (
              <div key={i} className="flex items-center justify-between px-5 py-3">
                <div className="flex items-center gap-3">
                  <span className={`font-inter text-[10px] font-semibold px-2 py-0.5 uppercase tracking-wider ${CATEGORY_COLORS[p.category] || 'text-zinc-400 bg-zinc-800'}`}>
                    {p.category}
                  </span>
                  <span className="font-inter text-sm text-white">{p.name}</span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="font-bebas text-base text-zinc-400">${p.price?.toFixed(2)}</span>
                  {p.status === 'active' ? (
                    <Eye size={13} className="text-green-500" />
                  ) : (
                    <EyeOff size={13} className="text-zinc-600" />
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Quick actions */}
        <div className="bg-[#1a1a1a] border border-white/10">
          <div className="px-5 py-4 border-b border-white/10">
            <h2 className="font-bebas text-lg text-white tracking-wider">QUICK ACTIONS</h2>
          </div>
          <div className="p-4 grid grid-cols-1 gap-3">
            {[
              { label: 'Add New Product', href: '/admin/products/new', icon: Package, color: 'bg-orange-500 hover:bg-orange-600' },
              { label: 'Edit Homepage Hero', href: '/admin/homepage', icon: Tag, color: 'bg-zinc-700 hover:bg-zinc-600' },
              { label: 'Create Campaign', href: '/admin/campaigns', icon: Tag, color: 'bg-zinc-700 hover:bg-zinc-600' },
              { label: 'Upload Media', href: '/admin/media', icon: Image, color: 'bg-zinc-700 hover:bg-zinc-600' },
            ].map(a => {
              const Icon = a.icon;
              return (
                <Link
                  key={a.href}
                  to={a.href}
                  data-testid={`quick-action-${a.label.toLowerCase().replace(/\s/g, '-')}`}
                  className={`flex items-center gap-3 ${a.color} text-white font-inter text-sm px-4 py-3 transition-colors`}
                >
                  <Icon size={15} />
                  {a.label}
                  <ChevronRight size={14} className="ml-auto" />
                </Link>
              );
            })}
          </div>

          {/* NFA reminder */}
          <div className="mx-4 mb-4 bg-orange-500/10 border border-orange-500/20 p-3">
            <p className="font-inter text-xs text-orange-400">
              <strong>Reminder:</strong> Suppressor products are NFA items. Ensure NFA Item flag is enabled for all suppressor listings.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
